/**
 * web/src/lib/flags.ts
 * Frontend feature-flag keys + the `useFlag` hook (the cosmetic half of the
 * hybrid model — see docs/FEATURE-FLAGS.md). The real boundary is the backend's
 * `require_flag` (404 when off); this just hides controls that would 404 anyway,
 * exactly like RoleGuard hides controls that would 403.
 *
 * `useFlag` reads the SERVER-evaluated `me.flags` map (delivered by /api/me),
 * NOT a client-only PostHog read — so the UI and the API agree on a flag's
 * state (both derive from the same server evaluation) and there's no
 * UI-shows-it / API-404s-it divergence window. posthog-js is still initialised
 * (see lib/posthog.ts) for identify + capture + future instant flips, but the
 * gate of record is the server's map. Defaults to OFF while `me` is loading so
 * an unreleased feature never flashes during the auth bootstrap.
 *
 * FLAGS keys MUST match the backend registry in `src/pixel/flags.py`.
 */
import { useAuth } from '../hooks/useAuth';

/** Known feature-flag keys — keep in sync with `pixel/flags.py::_FLAG_REGISTRY`. */
export const FLAGS = {
  /** Speaker onboarding workflow (roster, journey/spine, Confirmation gate). */
  speakerOnboarding: 'workflow-speaker-onboarding',
  /** Accommodation pickup report (Cvent Passkey housing + Personatech cross-ref). */
  accommodations: 'accommodations-pickup-report',
  /** Accommodation email agent — shadow-processed inbox (nothing is ever sent). */
  accommodationsEmailAgent: 'accommodations-email-agent',
  /** Content Intelligence — searchable session library + clip export (read-only vs Drive). */
  contentIntelligence: 'content-intelligence',
  /** Sponsor ROI reports — batch-generate per-sponsor recap decks from a PT export. */
  sponsorRoi: 'sponsor-roi-reports',
  /** Wave 2 ops automations — hotel space, design↔monday, production schedule, inventory, food trucks. */
  opsAutomations: 'ops-automations',
} as const;

export type FlagKey = (typeof FLAGS)[keyof typeof FLAGS];

/**
 * Returns whether `key` is enabled for the current user.
 *
 * @param key a flag key (use a `FLAGS.*` constant)
 * @returns true iff the server enabled it for this user; false while `me` is
 *   loading / unauthenticated, or when the flag is absent from the map.
 * @sideEffects subscribes to the /api/me React Query cache via useAuth
 * @idempotent yes — multiple calls share the underlying useMe cache entry
 */
export function useFlag(key: string): boolean {
  const { me } = useAuth();
  return me?.flags?.[key] ?? false;
}
